/**
 * Goal - Creates an extremely realistic, professional soccer goal.
 * Features: Balanced scale for game world, detailed aluminum construction,
 * realistic net, ground anchor system, and professional finish.
 */

import * as BABYLON from "babylonjs";

// Balanced dimensions for game world visibility
const GOAL_WIDTH = 12;      // Wide enough to be visible and challenging
const GOAL_HEIGHT = 4;      // Tall enough to be impressive
const POST_SIZE = 0.2;      // Square post dimensions - visible but not bulky
const CROSSBAR_WIDTH = 0.15; // Elliptical crossbar width
const CROSSBAR_HEIGHT = 0.12; // Elliptical crossbar height
const NET_DEPTH = 3;        // Net depth for realism

export class Goal {
  public posts: BABYLON.Mesh[] = [];
  public net: BABYLON.Mesh;
  private allMeshes: BABYLON.Mesh[] = [];

  constructor(
    scene: BABYLON.Scene,
    z: number,
    shadowGenerator?: BABYLON.ShadowGenerator
  ) {
    const isNorthGoal = z > 0;
    const netOffset = isNorthGoal ? NET_DEPTH / 2 : -NET_DEPTH / 2;
    const backZ = z + netOffset;

    // High-quality white aluminum material
    const aluminumMat = new BABYLON.PBRMaterial("aluminumMat", scene);
    aluminumMat.albedoColor = new BABYLON.Color3(0.98, 0.98, 0.99);
    aluminumMat.metallic = 0.15;
    aluminumMat.roughness = 0.25;
    aluminumMat.clearCoat.isEnabled = true;
    aluminumMat.clearCoat.intensity = 0.2;
    aluminumMat.clearCoat.roughness = 0.1;

    // Darker metal for supports
    const supportMat = new BABYLON.PBRMaterial("supportMat", scene);
    supportMat.albedoColor = new BABYLON.Color3(0.9, 0.9, 0.92);
    supportMat.metallic = 0.3;
    supportMat.roughness = 0.4;

    // Main structural components
    const leftPost = this.createMainPost(scene, aluminumMat, -GOAL_WIDTH / 2, z);
    const rightPost = this.createMainPost(scene, aluminumMat, GOAL_WIDTH / 2, z);
    const crossbar = this.createCrossbar(scene, aluminumMat, z);

    // Ground anchor system
    const leftAnchor = this.createGroundAnchorSystem(scene, aluminumMat, -GOAL_WIDTH / 2, z);
    const rightAnchor = this.createGroundAnchorSystem(scene, aluminumMat, GOAL_WIDTH / 2, z);

    // Diagonal support struts
    const leftStrut = this.createDiagonalStrut(scene, supportMat, -GOAL_WIDTH / 2, z, backZ);
    const rightStrut = this.createDiagonalStrut(scene, supportMat, GOAL_WIDTH / 2, z, backZ);

    // Lower support bars
    const leftLowerSupport = this.createLowerSupport(scene, supportMat, -GOAL_WIDTH / 2, z, backZ);
    const rightLowerSupport = this.createLowerSupport(scene, supportMat, GOAL_WIDTH / 2, z, backZ);

    // Back frame structure
    const leftBackPost = this.createBackPost(scene, aluminumMat, -GOAL_WIDTH / 2, backZ);
    const rightBackPost = this.createBackPost(scene, aluminumMat, GOAL_WIDTH / 2, backZ);
    const backTopBar = this.createBackTopBar(scene, aluminumMat, backZ);
    const backBottomBar = this.createBackBottomBar(scene, aluminumMat, backZ);

    // Corner reinforcement brackets
    const leftBracket = this.createCornerBracket(scene, aluminumMat, -GOAL_WIDTH / 2, GOAL_HEIGHT, z);
    const rightBracket = this.createCornerBracket(scene, aluminumMat, GOAL_WIDTH / 2, GOAL_HEIGHT, z);

    // Net support cables
    const topCable = this.createNetCable(scene, supportMat, GOAL_HEIGHT - 0.15, z, backZ);
    const bottomCable = this.createNetCable(scene, supportMat, 0.4, z, backZ);

    // Collect all structural parts for physics
    this.posts = [
      leftPost, rightPost, crossbar,
      leftAnchor, rightAnchor,
      leftStrut, rightStrut,
      leftLowerSupport, rightLowerSupport,
      leftBackPost, rightBackPost, backTopBar, backBottomBar,
      leftBracket, rightBracket,
      topCable, bottomCable
    ];
    this.allMeshes = [...this.posts];

    // Create realistic net (no physics - ball can pass through to score)
    this.net = this.createStadiumNet(scene, z, backZ);
    this.allMeshes.push(this.net);

    // Add physics to goal frame only (posts, crossbar, supports)
    // Net has no physics so ball can pass through to score
    this.posts.forEach((p) => {
      p.freezeWorldMatrix();
      p.physicsImpostor = new BABYLON.PhysicsImpostor(
        p,
        BABYLON.PhysicsImpostor.BoxImpostor,
        { mass: 0, restitution: 0.8, friction: 0.4 },
        scene
      );
    });

    // Shadows
    if (shadowGenerator) {
      this.allMeshes.forEach((m) => shadowGenerator.addShadowCaster(m));
    }
  }

  private createMainPost(scene: BABYLON.Scene, mat: BABYLON.PBRMaterial, x: number, z: number): BABYLON.Mesh {
    const post = BABYLON.MeshBuilder.CreateBox(
      "goalPost",
      { height: GOAL_HEIGHT, width: POST_SIZE, depth: POST_SIZE },
      scene
    );
    post.position = new BABYLON.Vector3(x, GOAL_HEIGHT / 2, z);
    post.material = mat;
    return post;
  }

  private createCrossbar(scene: BABYLON.Scene, mat: BABYLON.PBRMaterial, z: number): BABYLON.Mesh {
    const crossbar = BABYLON.MeshBuilder.CreateCylinder(
      "goalCrossbar",
      { height: GOAL_WIDTH + POST_SIZE, diameter: 1, tessellation: 32 },
      scene
    );
    crossbar.position = new BABYLON.Vector3(0, GOAL_HEIGHT, z);
    crossbar.rotation.z = Math.PI / 2;
    crossbar.scaling = new BABYLON.Vector3(CROSSBAR_HEIGHT, 1, CROSSBAR_WIDTH);
    crossbar.material = mat;
    return crossbar;
  }

  private createGroundAnchorSystem(scene: BABYLON.Scene, mat: BABYLON.PBRMaterial, x: number, z: number): BABYLON.Mesh {
    const basePlate = BABYLON.MeshBuilder.CreateBox(
      "goalBasePlate",
      { height: 0.12, width: POST_SIZE * 2.5, depth: POST_SIZE * 2.5 },
      scene
    );
    basePlate.position = new BABYLON.Vector3(x, 0.06, z);
    basePlate.material = mat;
    this.allMeshes.push(basePlate);

    const sleeve = BABYLON.MeshBuilder.CreateBox(
      "goalSleeve",
      { height: 0.6, width: POST_SIZE * 1.8, depth: POST_SIZE * 1.8 },
      scene
    );
    sleeve.position = new BABYLON.Vector3(x, 0.3, z);
    sleeve.material = mat;

    for (let i = 0; i < 4; i++) {
      const bolt = BABYLON.MeshBuilder.CreateCylinder(
        `anchorBolt${i}`,
        { height: 0.15, diameter: 0.05, tessellation: 8 },
        scene
      );
      const angle = (Math.PI / 2) * i + Math.PI / 4;
      const boltX = x + Math.cos(angle) * POST_SIZE;
      const boltZ = z + Math.sin(angle) * POST_SIZE;
      bolt.position = new BABYLON.Vector3(boltX, 0.075, boltZ);
      bolt.material = mat;
      this.allMeshes.push(bolt);
    }

    return sleeve;
  }

  private createDiagonalStrut(scene: BABYLON.Scene, mat: BABYLON.PBRMaterial, x: number, frontZ: number, backZ: number): BABYLON.Mesh {
    const length = Math.sqrt(Math.pow(NET_DEPTH, 2) + Math.pow(GOAL_HEIGHT * 0.5, 2));
    const strut = BABYLON.MeshBuilder.CreateCylinder(
      "goalStrut",
      { height: length, diameter: 0.1, tessellation: 16 },
      scene
    );

    const angle = Math.atan2(NET_DEPTH, GOAL_HEIGHT * 0.5);
    strut.position = new BABYLON.Vector3(x, GOAL_HEIGHT * 0.25, (frontZ + backZ) / 2);
    strut.rotation.x = frontZ > backZ ? -angle : angle;
    strut.material = mat;
    return strut;
  }

  private createLowerSupport(scene: BABYLON.Scene, mat: BABYLON.PBRMaterial, x: number, frontZ: number, backZ: number): BABYLON.Mesh {
    const support = BABYLON.MeshBuilder.CreateCylinder(
      "goalLowerSupport",
      { height: NET_DEPTH, diameter: 0.08, tessellation: 16 },
      scene
    );
    support.position = new BABYLON.Vector3(x, 0.25, (frontZ + backZ) / 2);
    support.rotation.x = Math.PI / 2;
    support.material = mat;
    return support;
  }

  private createBackPost(scene: BABYLON.Scene, mat: BABYLON.PBRMaterial, x: number, z: number): BABYLON.Mesh {
    const post = BABYLON.MeshBuilder.CreateBox(
      "goalBackPost",
      { height: GOAL_HEIGHT * 0.6, width: 0.12, depth: 0.12 },
      scene
    );
    post.position = new BABYLON.Vector3(x, GOAL_HEIGHT * 0.3, z);
    post.material = mat;
    return post;
  }

  private createBackTopBar(scene: BABYLON.Scene, mat: BABYLON.PBRMaterial, z: number): BABYLON.Mesh {
    const bar = BABYLON.MeshBuilder.CreateCylinder(
      "goalBackTopBar",
      { height: GOAL_WIDTH, diameter: 0.1, tessellation: 16 },
      scene
    );
    bar.position = new BABYLON.Vector3(0, GOAL_HEIGHT * 0.6, z);
    bar.rotation.z = Math.PI / 2;
    bar.material = mat;
    return bar;
  }

  private createBackBottomBar(scene: BABYLON.Scene, mat: BABYLON.PBRMaterial, z: number): BABYLON.Mesh {
    const bar = BABYLON.MeshBuilder.CreateCylinder(
      "goalBackBottomBar",
      { height: GOAL_WIDTH, diameter: 0.08, tessellation: 16 },
      scene
    );
    bar.position = new BABYLON.Vector3(0, 0.25, z);
    bar.rotation.z = Math.PI / 2;
    bar.material = mat;
    return bar;
  }

  private createCornerBracket(scene: BABYLON.Scene, mat: BABYLON.PBRMaterial, x: number, y: number, z: number): BABYLON.Mesh {
    const bracketGroup = new BABYLON.Mesh("bracketGroup", scene);
    bracketGroup.position = new BABYLON.Vector3(x, y, z);

    const horiz = BABYLON.MeshBuilder.CreateBox(
      "bracketHoriz",
      { height: 0.08, width: 0.25, depth: POST_SIZE * 1.2 },
      scene
    );
    horiz.position = new BABYLON.Vector3(x > 0 ? -0.12 : 0.12, 0, 0);
    horiz.parent = bracketGroup;
    horiz.material = mat;
    this.allMeshes.push(horiz);

    const vert = BABYLON.MeshBuilder.CreateBox(
      "bracketVert",
      { height: 0.25, width: POST_SIZE * 1.2, depth: 0.08 },
      scene
    );
    vert.position = new BABYLON.Vector3(0, -0.12, 0);
    vert.parent = bracketGroup;
    vert.material = mat;
    this.allMeshes.push(vert);

    return bracketGroup;
  }

  private createNetCable(scene: BABYLON.Scene, mat: BABYLON.PBRMaterial, y: number, frontZ: number, backZ: number): BABYLON.Mesh {
    const cable = BABYLON.MeshBuilder.CreateCylinder(
      "goalNetCable",
      { height: GOAL_WIDTH, diameter: 0.025, tessellation: 12 },
      scene
    );
    cable.position = new BABYLON.Vector3(0, y, (frontZ + backZ) / 2);
    cable.rotation.z = Math.PI / 2;
    cable.material = mat;
    return cable;
  }

  private createStadiumNet(scene: BABYLON.Scene, frontZ: number, backZ: number): BABYLON.Mesh {
    const netWidth = GOAL_WIDTH - 0.25;
    const netHeight = GOAL_HEIGHT - 0.15;
    const netDepth = Math.abs(backZ - frontZ) - 0.15;

    const net = BABYLON.MeshBuilder.CreateBox(
      "goalNet",
      { width: netWidth, height: netHeight, depth: netDepth },
      scene
    );

    net.position = new BABYLON.Vector3(
      0,
      netHeight / 2 + 0.08,
      (frontZ + backZ) / 2
    );

    const netMat = new BABYLON.StandardMaterial("stadiumNetMat", scene);
    netMat.diffuseColor = new BABYLON.Color3(0.92, 0.92, 0.94);
    netMat.specularColor = new BABYLON.Color3(0.01, 0.01, 0.01);
    netMat.specularPower = 1;
    netMat.alpha = 0.4;
    netMat.backFaceCulling = false;

    const netTexture = new BABYLON.DynamicTexture("stadiumNetPattern", { width: 512, height: 512 }, scene);
    const ctx = netTexture.getContext();

    ctx.clearRect(0, 0, 512, 512);
    ctx.strokeStyle = "#A0A0A0";
    ctx.lineWidth = 2;

    const hexSize = 24;
    const hexWidth = hexSize * Math.sqrt(3);
    const hexHeight = hexSize * 2;

    for (let row = -2; row < 24; row++) {
      for (let col = -2; col < 24; col++) {
        const x = col * hexWidth + (row % 2) * (hexWidth / 2);
        const y = row * hexHeight * 0.75;
        this.drawHexagon(ctx, x, y, hexSize);
      }
    }

    netTexture.update();
    netMat.diffuseTexture = netTexture;
    netMat.opacityTexture = netTexture;

    net.material = netMat;
    net.freezeWorldMatrix();

    return net;
  }

  private drawHexagon(ctx: BABYLON.ICanvasRenderingContext, x: number, y: number, size: number): void {
    ctx.beginPath();
    for (let i = 0; i < 6; i++) {
      const angle = (Math.PI / 3) * i;
      const hx = x + size * Math.cos(angle);
      const hy = y + size * Math.sin(angle);
      if (i === 0) {
        ctx.moveTo(hx, hy);
      } else {
        ctx.lineTo(hx, hy);
      }
    }
    ctx.closePath();
    ctx.stroke();
  }
}

/** Half-width used by ScoreSystem for goal detection */
export const GOAL_HALF_WIDTH = GOAL_WIDTH / 2;
